<template>
	<v-autocomplete
		ref="autocomplete"
		:items="items"
		:label="displayLabel"
		:rules="required ? [rules.required] : []"
		v-model="metaValue"
		:class="classArray"
		:item-text="itemText"
		:item-value="itemValue"
		:return-object="returnObject"
		:search-input.sync="keyword"
		dense
		outlined
		:hide-details="hideDetails"
		:hint="hint ? hint : defaultHint"
		:no-data-text="hint ? hint : defaultHint"
		:persistent-hint="persistentHint"
		:clearable="clearable"
		@keyup="setSearchTimer"
		@change="selectItem($event)"
	>
		<template v-slot:append>
			<slot name="append" />
		</template>
		<template v-slot:append-outer>
			<slot name="append-outer" />
		</template>
		<template v-if="pagination.next && !searching" v-slot:append-item>
			<v-list-item @click="fetchNextItems">
				<v-list-item-content>
					<v-list-item-title>さらに結果を取得する</v-list-item-title>
				</v-list-item-content>
			</v-list-item>
		</template>
	</v-autocomplete>
</template>

<script>
import axios from "axios"
import Qs from "qs"
import rules from "../../helper/rules"
import authenticate from "../../helper/authenticate"
import errorHandling from "../../helper/errorHandling"
import PaginationModule from "./PaginationModule.vue"

export default {
	components: {
		PaginationModule,
	},
	props: {
		// ラベル
		label: {
			type: String,
			required: false,
		},
		// syncを使う、選択時に返す値
		value: {
			type: null,
			required: true,
		},
		// 選択した値をemitで受け取る場合はtrue
		// returnObjectWhenEmitの時は、returnObjectがtrueである必要がある
		requireEmit: {
			type: Boolean,
			required: false,
			default: false,
		},
		// returnObjectがなくても、emitでobjectを返す
		returnObjectWhenEmit: {
			type: Boolean,
			required: false,
			default: false,
		},
		// ヒントをpropsで送る場合
		hint: {
			type: String,
			required: false,
		},
		// インクリメンターサーチのアクセス先
		url: {
			type: String,
			required: true,
		},
		// クラス名
		classArray: {
			type: Array,
			required: false,
			default: () => [],
		},
		// requiredの場合は必須項目となる
		required: {
			type: Boolean,
			default: false,
		},
		// セレクトのラベルを取得するためのキー
		itemText: {
			type: String,
			required: false,
			default: "name",
		},
		// セレクトの値を取得するためのキー
		itemValue: {
			type: String,
			required: false,
			default: "id",
		},
		// 最初にセレクトに入れる配列
		initialItems: {
			type: Array,
			required: false,
			default: () => [],
		},
		// ransackのクエリパラメーターのキー
		keywordKey: {
			type: String,
			required: false,
			default: "name_cont",
		},
		// レスポンスのメインの配列のキー
		// 指定しない場合は、res.dataがそのまま配列として使われる
		responseItemKey: {
			type: String,
			required: false,
			default: "items",
		},
		// データ(モデル)の名前
		targetName: {
			type: String,
			required: false,
			default: "データ",
		},
		// データして未選択の時のヒント
		dataFetchedHint: {
			type: String,
			required: false,
		},
		// データを取得中の時のヒント
		searchingHint: {
			type: String,
			required: false,
		},
		// データがない時のヒント
		noDataText: {
			type: String,
			required: false,
		},
		// 入力されていない時のヒント
		emptyFieldHint: {
			type: String,
			required: false,
		},
		// Valueが存在する時のヒント
		fieldFilledHint: {
			type: String,
			required: false,
		},
		// ヒントを常に表示するかどうか
		persistentHint: {
			type: Boolean,
			required: false,
			default: false,
		},
		// オブジェクトで返すかどうか
		returnObject: {
			type: Boolean,
			required: false,
			default: false,
		},
		// v-autocompleteにclearableを設定するかどうか
		clearable: {
			type: Boolean,
			required: false,
			default: false,
		},
		hideDetails: {
			type: Boolean,
			required: false,
			default: false,
		},
		// 送るparameter
		params: {
			type: Object,
			required: false,
			default: () => ({}),
		},
		// qに追加されるparameter ransack用
		query: {
			type: Object,
			required: false,
			default: () => ({}),
		},
	},
	data() {
		return {
			dialog: false,
			items: [],
			searchTimer: null,
			keyword: "",
			rules: rules,
			searching: false,
			pagination: {
				count: null,
				current: 1,
				limit_value: null,
				next: null,
				pages: null,
				previous: null,
			},
			ignoreKeys: [
				"ArrowDown",
				"ArrowUp",
				"Enter",
				"Escape",
				"Shift",
				"Eisu",
				"Kana",
				"ArrowLeft",
				"ArrowRight",
				"KanjiMode",
				"Tab",
				"CapsLock",
				"Control",
				"Alt",
				"Meta",
				"ContextMenu",
				"F1",
				"F2",
				"F3",
				"F4",
				"F5",
				"F6",
				"F7",
				"F8",
				"F9",
				"F10",
				"F11",
				"F12",
				"Home",
			],
		}
	},
	mounted() {
		this.items = this.initialItems
	},
	methods: {
		setSearchTimer(e) {
			if (this.fetchNotRequired(e)) return
			if (this.ignoreKeys.includes(e.key)) return // 無視するキーの場合はreturn
			this.items = []
			this.keyword = e.target.value
			clearTimeout(this.searchTimer) // 連続でタイプした時用に前のタイマーをキャンセル
			if (!this.keyword || !this.keyword.length) return // 0文字の場合はreturn
			this.searchTimer = setTimeout(this.fetchItems, 500) // 連続でタイプするとアクセスが走りすぎるため.5秒後に発火
		},
		fetchNotRequired(e) {
			if (e == null) {
				// nullなら不必要
				return true
			} else if (this.returnObject) {
				//  returnObjectかつeがmetaValueのテキストと同様なら選択直後なので不必要
				return this.metaValue != null && this.metaValue[this.itemText] == e
			} else {
				//  returnObjectでないかつeとmetaValueのitemが同じなら選択直後なので不必要
				const item = this.items.find((item) => item[this.itemValue] == this.metaValue)
				return this.metaValue != null && e == item[this.itemText]
			}
		},
		fetchItems() {
			this.searching = true
			const params = {
				q: {
					[this.keywordKey]: this.keyword,
					...this.query,
				},
				page: 1,
				...this.params,
			}
			const paramsSerializer = (params) => Qs.stringify(params, { arrayFormat: "brackets" })
			const headers = authenticate.setHeader()
			axios
				.get(this.url, {
					headers,
					params,
					paramsSerializer,
				})
				.then((res) => {
					this.searchTimer = null
					this.pagination = res.data.pagination
					this.items = res.data[this.responseItemKey]
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
				.finally(() => {
					this.searching = false
				})
		},
		fetchNextItems() {
			event.preventDefault()
			this.searching = true
			const params = {
				q: {
					[this.keywordKey]: this.keyword,
				},
				page: this.pagination.next,
			}
			const paramsSerializer = (params) => Qs.stringify(params, { arrayFormat: "brackets" })
			const headers = authenticate.setHeader()
			axios
				.get(this.url, {
					headers,
					params,
					paramsSerializer,
				})
				.then((res) => {
					this.searchTimer = null
					this.items = this.items.concat(res.data[this.responseItemKey])
					this.pagination = res.data.pagination
				})
				.catch((err) => {
					errorHandling.errorResponse(err)
				})
				.finally(() => {
					this.searching = false
				})
		},
		selectItem(e) {
			this.requireEmit ? this.$emit("change", this.item(e)) : false
		},
		item(e) {
			let item = this.items.find((item) =>
				this.returnObject ? item == e : item[this.itemValue] == e
			)
			if (item == undefined) return null
			return this.returnObject || this.returnObjectWhenEmit ? item : item[this.itemValue]
		},
	},
	computed: {
		metaValue: {
			get() {
				return this.value
			},
			set(newVal) {
				return this.$emit("update:value", newVal)
			},
		},
		defaultHint() {
			if (this.items.length && !this.metaValue) {
				return this.dataFetchedHint ? this.dataFetchedHint : `${this.targetName}を選択してください`
			} else if (this.searchTimer || this.searching) {
				return this.searchingHint ? this.searchingHint : `${this.targetName}を取得しています`
			} else if (!this.items.length && this.keyword) {
				return this.noDataText ? this.noDataText : `該当する${this.targetName}が存在していません`
			} else if (!this.metaValue) {
				return this.emptyFieldHint ? this.emptyFieldHint : `${this.targetName}を入力してください`
			} else if (this.metaValue) {
				return this.fieldFilledHint ? this.fieldFilledHint : ``
			}
		},
		displayLabel() {
			if (this.label) {
				return this.required ? `${this.label}（必須）` : this.label
			}
		},
	},
	watch: {
		initialItems: {
			handler(val) {
				this.items = val
			},
			deep: true,
		},
	},
}
</script>
